import { SpwkUtil } from "../../dao/SpwkUtil";
import { SpwkUtilV3 } from "../../dao/SpwkUtilV3";
import { UniForm } from "../../dao/UniForm";
import * as yup from "yup";
import { UniInput, UniInputKind } from "../../dao/UniInput";
import { Is連動排除的題目, UniQuestion } from "../../dao/UniQuestion";
import { new_ErrorAlert } from "../../export_spwk/ExportManager";
import { TypeOfShape } from "yup/lib/object";
import { FieldValues, UseFormReturn } from "react-hook-form";
import _ from "lodash";
import dayjs from "dayjs";
import { Project } from "../../dao/Project";
import i18n from "../../../i18n";

export class YupSchemaBuilder {
  static CreateByChapter(
    rhf: UseFormReturn<FieldValues, any>,
    chapter: string,
    _SpwkBaseForm: UniForm,
    _UniFormList: UniForm[],
    project: Project
  ) {
    /* 示意圖:
    { <--- yup_shape

      作業經歷: { <--- yup_chapter
          基本資料●姓名●input_1: yup_input_valid() <--- 加入<非選必填>
          基本資料●性別●input_1: yup_input_valid() <--- 加入<非選必填>
          ...

        ┬ 工作班別: <--- 加入<單選必選>                         (單選: 固定/兩班/四班/其他:_____)
        └ 工作班別●其他●input_1: <--- 加入<單選有勾則必填> 
        ...

      }.test() { <--- 加入<多選必填驗證>

        errors = [];

        for All 多選題列表 {
          if(未勾選任何一個項目){
            errors.add(error)
          }          
        }

        return errors;
      }

    }
    

  
    */
    /** 取得指定章節合併後的題目，並排除不須驗證的標題 */
    let chapter_questions = SpwkUtil.GetChapterQuestionsV3(
      chapter,
      _SpwkBaseForm,
      _UniFormList
    );
    chapter_questions = chapter_questions.filter(
      (q) => q.kind !== SpwkUtilV3.Q_KIND_標題
    );

    /** 此處排除畫面上不顯示的元件，看不到的不需驗證 */
    chapter_questions = chapter_questions.filter(
      (x) => !Is連動排除的題目(x, rhf)
    );

    let shape_非選必填 = YupSchemaBuilder.GetShape_非選必填(
      chapter,
      chapter_questions
    );

    let shape_單選必填 = YupSchemaBuilder.GetShape_單選必填(
      chapter,
      chapter_questions
    );
    let shape_單選有勾時_Input必填 =
      YupSchemaBuilder.GetShape_單選有勾時_Input必填(
        chapter,
        chapter_questions
      );

    let shape_多選有勾時_Input必填 =
      YupSchemaBuilder.GetShape_多選有勾時_Input必填(
        chapter,
        chapter_questions
      );

    // FIXME: 注意! 個驗證如果會重疊時，不可以這樣簡單加入，要另外小心的一一比對加入
    // yup.test().test()... 可能可以無限往後接，遇到不為空的節點，應該把 test() 往後加入
    // 正確的方法之後用到再上網找
    let yup_chapter: any = {
      ...shape_非選必填,

      ...shape_單選必填,
      ...shape_單選有勾時_Input必填,

      ...shape_多選有勾時_Input必填,
    };

    const yup_shape: any = {};
    yup_shape[chapter] = yup
      .object()
      .shape(yup_chapter)
      .test("", "error message", (rhf_data) => {
        return Test_多選題至少勾一個(rhf_data, chapter, chapter_questions);
      });

    const yup_schema = yup
      .object()
      .test("", "error message", (rhf_value: any) => {
        return YupGlobalValidation(rhf_value, project);
      })
      .shape(yup_shape);
    return yup_schema;
  }

  /**
   * 對所有非選題底下的 Inputs，加入 yup 驗證 (一般是判斷必填)
   */
  private static GetShape_非選必填(
    chapter: string,
    chapter_questions: UniQuestion[]
  ) {
    /*
      示意圖:
      { <--- yup_shape

        作業經歷: { <--- yup_chapter

            基本資料●姓名●input_1: yup_input_valid() <--- 加入
            基本資料●性別●input_1: yup_input_valid() <--- 加入
            ...
        }
      }

    */
    const yup_chapter: any = {};

    const questions = chapter_questions.filter(
      (q) => q.kind === SpwkUtil.KIND_非選
    );

    for (const question of questions) {
      if (question.Options == null) {
        throw new_ErrorAlert("question.Options == null");
      }

      for (const option of question.Options) {
        if (option.Inputs == null) {
          throw new_ErrorAlert("option.Inputs == null");
        }

        for (const input of option.Inputs) {
          let input_rhf_name = option.get_Input_RhfName(input.input_code);
          input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter);

          yup_chapter[input_rhf_name] = YupValid_Input必填(input);
        }
      }
    }

    return yup_chapter;
  }

  static GetShape_單選必填(chapter: string, chapter_questions: UniQuestion[]) {
    const yup_chapter: any = {};

    /** 單選 必填 */
    const questions = chapter_questions.filter(
      (q) => q.kind === SpwkUtil.KIND_單選
    );
    for (const question of questions) {
      yup_chapter[question.question_code] =
        YupSchemaBuilder.YupValid_單選必填(question);
    }

    return yup_chapter;
  }

  static YupValid_單選必填(question: UniQuestion) {
    if (question.kind !== SpwkUtilV3.Q_KIND_單選) {
      throw new_ErrorAlert("616843151 必須是單選題 !!!");
    }

    return yup.string().test("", "error message", (rhf_value) => {
      if (rhf_value == null || rhf_value === "") {
        // 單選 未填寫數值
        let e1 = new yup.ValidationError(
          `此題至少選一個`,
          null,
          `${question.question_code}`
        ); //單選題沒選擇, 丟出錯誤訊息
        return new yup.ValidationError([e1]);
      } else {
        return true;
      }
    });
  }

  static GetShape_單選有勾時_Input必填(
    chapter: string,
    chapter_questions: UniQuestion[]
  ) {
    const yup_chapter: any = {};

    const questions = chapter_questions.filter(
      (q) => q.kind === SpwkUtil.KIND_單選
    );

    /** 單選: 有勾時，要依照有 Input 條件驗證，必須合法 */
    for (const question of questions) {
      if (question.Options == null) {
        throw new_ErrorAlert("4949465 question.Options ==null !!!");
      }

      for (const option of question.Options) {
        if (option.Inputs == null) {
          throw new_ErrorAlert("option.Inputs == null !!!");
        }

        if (option.Inputs.length === 0) {
          continue; // pass; 沒有 Input，不須處理
        }

        for (const input of option.Inputs) {
          let q_rhf_name = question.get_Question_RhfName();
          q_rhf_name = RemoveChapterPrefix(q_rhf_name, chapter); // 其實目前相當於 question_code

          let input_rhf_name = option.get_Input_RhfName(input.input_code);
          input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

          yup_chapter[input_rhf_name] = yup.string().when([q_rhf_name], {
            is: input.option_code, // if(rhf_value === "答案") {... =有勾選時
            then: YupValid_Input必填(input), // 才觸發這個驗證
          });
        }
      }
    }

    return yup_chapter;
  }

  static GetShape_多選有勾時_Input必填(
    chapter: string,
    chapter_questions: UniQuestion[]
  ) {
    const yup_chapter: any = {};

    const questions = chapter_questions.filter(
      (q) => q.kind === SpwkUtil.KIND_多選
    );

    /** 多選: 有勾時，要依照有 Input 條件驗證，必須合法 */
    for (const question of questions) {
      if (question.Options == null) {
        throw new_ErrorAlert("4949465 question.Options ==null !!!");
      }

      for (const option of question.Options) {
        if (option.Inputs == null) {
          throw new_ErrorAlert("1468431 option.Inputs == null !!!");
        }

        if (option.Inputs.length === 0) {
          continue; // pass; 沒有 Input，不須處理
        }

        for (const input of option.Inputs) {
          let option_rhf_name = option.get_Option_RhfName();
          option_rhf_name = RemoveChapterPrefix(option_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

          let input_rhf_name = option.get_Input_RhfName(input.input_code);
          input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

          let target_name = option_rhf_name; // 誰勾起來，才要判斷必填 rhf[target_name]===true 時，才要判斷必填
          yup_chapter[input_rhf_name] = yup.string().when([target_name], {
            is: true, // if(rhf_value === "答案") {... =有勾選時
            then: YupValid_Input必填(input), // 才觸發這個驗證
          });
        }
      }
    }

    return yup_chapter;
  }
}

/**
 * 取得單一 Input 的 yup驗證
 */
function YupValid_Input必填(input: UniInput) {
  let validation;

  if (input.input_kind === UniInputKind.Text) {
    validation = yup.string().required("請輸入");
  } else if (input.input_kind === UniInputKind.Select) {
    validation = yup.string().required("請點選");
  } else if (input.input_kind === UniInputKind.DateTime) {
    validation = yup.string().nullable().required("請點選");
  } else if (input.input_kind === UniInputKind.Number) {
    validation = yup.string().nullable().required("請輸入");
  } else if (input.input_kind === UniInputKind.Int) {
    validation = yup.string().nullable().required("請輸入");
  } else {
    throw new_ErrorAlert(
      `未知的 input.input_kind=${input.input_kind} !!!\ninput.guid: ${input.guid}`
    );
  }

  return validation.test(
    "no-whitespace-and-commas",
    "請填寫有效的內容",
    (value) => {
      if (value && /^\s+$/.test(value)) {
        return false;
      }

      return true; // 驗證通過
    }
  );
  // .test('no-whitespace-and-commas', '請不要輸入,或，', (value) => {
  //   if (value && /[,，]/.test(value)) {
  //     return false;
  //   }
  //   return true; // 驗證通過
  // });
}

/**
 * 原始rhf_name = 基本資料.基本資料●身分字號●input_1
 * 移除開頭的: "基本資料." 才能正確加入 yup 結構
 */
function RemoveChapterPrefix(rhf_name: string, chapter: string): string {
  return rhf_name.substring((chapter + ".").length);
}

function Is此多選題是否至少勾選一個項目(
  chapter: string,
  rhf_data: any,
  question: UniQuestion
) {
  let q_data_obj = undefined;

  if (
    chapter === SpwkUtilV3.CHAPTER_既往病史 ||
    chapter === SpwkUtilV3.CHAPTER_自覺症狀
  ) {
    // CH4、CH6 這兩大題，答案都混在這個 data
    // 下面再對所有 此傳入的單一Question.Options 檢查，是否至少有一個選項是true
    q_data_obj = rhf_data;
  } else {
    // 對於一般的題目，個別答案在個別的 欄位中
    // 先取出來，再檢查 Options 檢查，是否至少有一個選項是true
    q_data_obj = rhf_data[question.question_code];
  }

  let has_check = false;

  if (q_data_obj == null) {
    has_check = false; // 可能還沒填寫
  } else {
    if (question.Options == null) {
      throw new_ErrorAlert("3541616 question.Options==null !!!");
    }

    // const fields = Object.keys(q_data_obj);// ["造船", "開礦", "紡織"... ] // 假設之前有勾的選項，現在不再畫面上，有可能會沒有判斷到，最好照著 question.Options.code 跑過一次檢查會比較準
    const fields = question.Options.map((x) => x.option_code); // ["造船", "開礦", "紡織"... ]

    for (const field of fields) {
      let tf = q_data_obj[field] as boolean;
      if (tf === true) {
        has_check = true;
        break;
      }
    }
  }

  return has_check;
}

function Test_多選題至少勾一個(
  rhf_data: TypeOfShape<any>,
  chapter: string,
  chapter_questions: UniQuestion[]
): boolean | yup.ValidationError | Promise<boolean | yup.ValidationError> {
  let questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtilV3.Q_KIND_多選
  );
  let yup_errors: yup.ValidationError[] = [];

  for (const question of questions) {
    if (question.is_required === "0") {
      continue;
    }
    let has_check = Is此多選題是否至少勾選一個項目(chapter, rhf_data, question);
    if (has_check === false) {
      let e1 = new yup.ValidationError(
        "此題至少選一個",
        null,
        question.question_code
      );
      yup_errors.push(e1);
    }
  }

  if (yup_errors.length === 0) {
    return true;
  } else {
    return new yup.ValidationError(yup_errors);
  }
}

/**
 全域自訂驗證，自己寫規則
*/
function YupGlobalValidation(
  rhf_value: any,
  project: Project
): boolean | yup.ValidationError | Promise<boolean | yup.ValidationError> {
  let yup_errors: yup.ValidationError[] = [];

  // return true;

  let 出生日期 = GetDayJS(rhf_value, SpwkUtilV3.RHF_NAME_出生日期); //dayjs(_.get(rhf_value, SpwkUtilV3.RHF_NAME_出生日期));
  let 受雇日期 = GetDayJS(rhf_value, SpwkUtilV3.RHF_NAME_基本資料_受雇日期);
  let 曾經從事_起始日期 = GetDayJS(
    rhf_value,
    SpwkUtilV3.RHF_NAME_曾經從事_起始日期
  ); //dayjs(_.get(rhf_value, SpwkUtilV3.RHF_NAME_曾經從事_起始日期));
  let 曾經從事_截止日期 = GetDayJS(
    rhf_value,
    SpwkUtilV3.RHF_NAME_曾經從事_截止日期
  ); //dayjs(_.get(rhf_value, SpwkUtilV3.RHF_NAME_曾經從事_截止日期));
  let 目前從事_起始日期 = GetDayJS(
    rhf_value,
    SpwkUtilV3.RHF_NAME_目前從事_起始日期
  ); //dayjs(_.get(rhf_value, SpwkUtilV3.RHF_NAME_目前從事_起始日期));
  let 目前從事_截止日期 = GetDayJS(
    rhf_value,
    SpwkUtilV3.RHF_NAME_目前從事_截止日期
  ); //dayjs(_.get(rhf_value, SpwkUtilV3.RHF_NAME_目前從事_截止日期));
  // 判斷工作時必須大於16歲
  //Yao
  if (曾經從事_起始日期 != null && 出生日期 != null) {
    const yearsDiff = 曾經從事_起始日期.diff(出生日期, "year");
    const AGE_LIMIT = 16;
    if (yearsDiff < AGE_LIMIT) {
      if (project.yup_pass === "1") {
        //pass
      } else {
        yup_errors.push(
          new yup.ValidationError(
            stringFormat(i18n.t("工作時不可小於{0}歲"), AGE_LIMIT),
            null,
            SpwkUtilV3.RHF_NAME_曾經從事_起始日期,
            SpwkUtilV3.YUP_ERROR_TYPE_FROM
          )
        );
      }
    }
  }
  if (目前從事_起始日期 != null && 出生日期 != null) {
    const yearsDiff = 目前從事_起始日期.diff(出生日期, "year");
    const AGE_LIMIT = 16;
    if (yearsDiff < AGE_LIMIT) {
      if (project.yup_pass === "1") {
        //pass
      } else {
        yup_errors.push(
          new yup.ValidationError(
            stringFormat(i18n.t("工作時不可小於{0}歲"), AGE_LIMIT),
            null,
            SpwkUtilV3.RHF_NAME_目前從事_起始日期,
            SpwkUtilV3.YUP_ERROR_TYPE_FROM
          )
        );
      }
    }
  }

  // 判斷: 曾經從事 目前從事 不重疊
  if (曾經從事_截止日期 != null && 目前從事_起始日期 != null) {
    if (曾經從事_截止日期.isAfter(目前從事_起始日期)) {
      yup_errors.push(
        new yup.ValidationError(
          stringFormat(
            i18n.t("曾經從事_截止日期:{0} 必須早於 目前從事_起始日期:{1}!"),
            曾經從事_截止日期.format("YYYY/MM/DD"),
            目前從事_起始日期.format("YYYY/MM/DD")
          ),
          null,
          SpwkUtilV3.RHF_NAME_曾經從事_截止日期,
          SpwkUtilV3.YUP_ERROR_TYPE_FROM
        )
      );
    }
  }

  // 判斷: 起訖日前後正確
  if (曾經從事_起始日期 != null && 曾經從事_截止日期 != null) {
    if (曾經從事_起始日期.isAfter(曾經從事_截止日期)) {
      yup_errors.push(
        new yup.ValidationError(
          stringFormat(
            i18n.t("曾經從事_起始日期:{0} 必須早於 曾經從事_截止日期:{1}!"),
            曾經從事_起始日期.format("YYYY/MM/DD"),
            曾經從事_截止日期.format("YYYY/MM/DD")
          ),
          null,
          SpwkUtilV3.RHF_NAME_曾經從事_截止日期,
          SpwkUtilV3.YUP_ERROR_TYPE_FROM
        )
      );
    }
  }

  // 判斷: 起訖日前後正確
  if (目前從事_起始日期 != null && 目前從事_截止日期 != null) {
    if (目前從事_起始日期.isAfter(目前從事_截止日期)) {
      yup_errors.push(
        new yup.ValidationError(
          stringFormat(
            i18n.t("目前從事_起始日期:{0} 必須早於 目前從事_截止日期:{1}!"),
            目前從事_起始日期.format("YYYY/MM/DD"),
            目前從事_截止日期.format("YYYY/MM/DD")
          ),
          null,
          SpwkUtilV3.RHF_NAME_目前從事_截止日期,
          SpwkUtilV3.YUP_ERROR_TYPE_FROM
        )
      );
    }
  }
  if (目前從事_起始日期 != null && 受雇日期 != null) {
    if (受雇日期.isAfter(目前從事_起始日期)) {
      yup_errors.push(
        new yup.ValidationError(
          stringFormat(
            i18n.t("受雇日期: {0} 必須早於 目前從事_起始日期: {1}!"),
            受雇日期.format("YYYY/MM/DD"),
            目前從事_起始日期.format("YYYY/MM/DD")
          ),
          null,
          SpwkUtilV3.RHF_NAME_基本資料_受雇日期,
          SpwkUtilV3.YUP_ERROR_TYPE_FROM
        )
      );
    }
  }
  if (dayjs(目前從事_起始日期).isValid() && dayjs(受雇日期).isValid()) {
    //pass
  } else if (
    dayjs(受雇日期).isValid() === true &&
    dayjs(目前從事_起始日期).isValid() === false
  ) {
    yup_errors.push(
      new yup.ValidationError(
        i18n.t(`請填寫目前從事_起始日期`),
        null,
        SpwkUtilV3.RHF_NAME_目前從事_截止日期,
        SpwkUtilV3.YUP_ERROR_TYPE_FROM
      )
    );
    return true;
  } else if (
    dayjs(受雇日期).isValid() === false &&
    dayjs(目前從事_起始日期).isValid() === true
  ) {
    yup_errors.push(
      new yup.ValidationError(
        i18n.t(`請填寫受雇日期`),
        null,
        SpwkUtilV3.RHF_NAME_目前從事_截止日期,
        SpwkUtilV3.YUP_ERROR_TYPE_FROM
      )
    );
  } else {
    yup_errors.push(
      new yup.ValidationError(
        i18n.t(`請填寫受雇日期`),
        null,
        SpwkUtilV3.RHF_NAME_目前從事_截止日期,
        SpwkUtilV3.YUP_ERROR_TYPE_FROM
      )
    );
  }

  if (yup_errors.length === 0) {
    return true;
  } else {
    return new yup.ValidationError(yup_errors);
  }
}

function GetDayJS(rhf_value: any, rhf_name: string) {
  let raw_date = _.get(rhf_value, rhf_name);

  if (raw_date == null || raw_date === "" || ("" + raw_date).trim() === "") {
    return null;
  } else {
    return dayjs(raw_date);
  }
}

function stringFormat(template: string, ...args: any[]) {
  return template.replace(/{(\d+)}/g, (match, number) => {
    return typeof args[number] !== "undefined" ? args[number] : match;
  });
}
